import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { hubService } from '../../services/hub.service';
import { hubClient } from '../../services/hubClient';
import { isBotSystem } from '../../lib/botNav';
import { errorText } from '../../lib/assistantView';
import { AiConfigModal } from '../aiconfig/AiConfigModal';
import { CyrillicNote } from '../quality/CyrillicNote';
import { RecentBlockQuestions } from './RecentBlockQuestions';
import {
  bundleTextLoadNotice, bundleTextSummary, CHANNEL_LABEL, descriptionHint, distinctExamples, exampleHint, exampleLimitHint, hitBand, lastSentLabel,
  linkStatsLabel, linkStatus, neverClosestHint, parseExamples, preselect, scoreLabel, V2_LINK_CHANNELS, withExample,
  type BlockLinkRow, type BundleTextRow, type LinkStat, type LinkThresholds, type PickerBlock,
} from '../../lib/blockLinksView';

const input = 'rounded border border-gray-300 px-3 py-2 text-sm';

interface Draft { id: string | null; blockRef: string; blockName: string; description: string; examples: string; enabled: boolean }
const empty: Draft = { id: null, blockRef: '', blockName: '', description: '', examples: '', enabled: true };
type Hit = { link: BlockLinkRow; score: number; via: string };

/**
 * Knowledge › Blocks (knowledge→block plan 2): the blocks the AI may send, each with "when to send it" and example
 * questions. A link points at the block (never copies it); a deleted block shows red — re-pick or remove. v2 links are
 * per channel: list and picker show one channel. Sending starts with plan 3.
 */
export function BlocksPanel({ system, botId }: { system: string; botId: string }) {
  const v2 = system === 'chatbot_v2';
  const [links, setLinks] = useState<BlockLinkRow[] | null>(null);
  const [checked, setChecked] = useState(true);
  const [thresholds, setThresholds] = useState<LinkThresholds>({ blockOnly: 0.8, attach: 0.6 });
  const [channel, setChannel] = useState<string>('FACEBOOK');
  const [draft, setDraft] = useState<Draft | null>(null);
  const [q, setQ] = useState('');
  const [blocks, setBlocks] = useState<PickerBlock[] | null>(null);
  const [blocksDown, setBlocksDown] = useState(false);
  const [pickerError, setPickerError] = useState<string | null>(null);
  const pickerSeq = useRef(0);
  const [question, setQuestion] = useState('');
  const [hits, setHits] = useState<Hit[] | null>(null);
  // the Cyrillic a Latin test question was searched as (what a customer's message becomes)
  const [searchedAs, setSearchedAs] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // "Before switching on": bundle text the documents lack. Failing to load it shows a warning line, nothing else breaks.
  const [bundleText, setBundleText] = useState<{ bundles: BundleTextRow[]; truncated: boolean } | null>(null);
  const [bundleTextFailed, setBundleTextFailed] = useState(false);
  const [copied, setCopied] = useState<Record<string, string>>({});
  const [copying, setCopying] = useState<string | null>(null);
  const [copyError, setCopyError] = useState<string | null>(null);
  // Closest / sent counts per link over the last days (answer traces). Failing to load hides the line, nothing else.
  const [stats, setStats] = useState<{ days: number; decisions: number; links: Record<string, LinkStat> } | null>(null);

  // Deep link `?block=&channel=` (from the block editors): read on the first render, applied once the links have
  // loaded, then dropped from the URL so a reload or tab switch does not reopen it.
  const [params, setParams] = useSearchParams();
  const [pending, setPending] = useState(() => preselect(params, system));
  const hadParams = useRef(params.has('block') || params.has('channel'));

  const k = useCallback(
    <T,>(method: 'GET' | 'POST' | 'PUT' | 'DELETE', rest: string, body?: unknown) =>
      hubService.knowledge<T>(system, botId, method, `block-links${rest}`, body),
    [system, botId],
  );

  const load = useCallback(async () => {
    try {
      const r = await k<{ links: BlockLinkRow[]; checked: boolean; thresholds?: LinkThresholds }>('GET', '');
      setLinks(r.links);
      setChecked(r.checked);
      if (r.thresholds) setThresholds(r.thresholds);
    } catch (e) {
      setLinks([]);
      setError(errorText(e));
    }
  }, [k]);

  useEffect(() => {
    void load();
  }, [load]);

  // v2: counts and recent questions follow the channel picked above; v1 blocks serve every channel.
  const viewChannel = v2 ? channel : undefined;
  useEffect(() => {
    if (!isBotSystem(system)) return;
    let live = true;
    hubClient.blockLinkStats({ system, botId }, 7, viewChannel)
      .then((r) => { if (live) setStats(r); })
      .catch(() => { if (live) setStats(null); });
    return () => { live = false; };
  }, [system, botId, viewChannel]);

  useEffect(() => {
    let live = true;
    k<{ bundles: BundleTextRow[]; truncated: boolean }>('GET', '/bundle-text')
      .then((r) => { if (live) { setBundleText(r); setBundleTextFailed(false); } })
      .catch(() => { if (live) { setBundleText(null); setBundleTextFailed(true); } });
    return () => { live = false; };
  }, [k]);

  async function copyBundle(b: BundleTextRow) {
    setCopying(b.bundleId);
    setCopyError(null);
    try {
      const r = await k<{ document: { id: string; title: string } }>('POST', `/bundle-text/${encodeURIComponent(b.bundleId)}/copy`);
      setCopied((c) => ({ ...c, [b.bundleId]: r.document.title }));
    } catch (e) {
      setCopyError(errorText(e));
    } finally {
      setCopying(null);
    }
  }

  useEffect(() => {
    if (!hadParams.current || links === null) return;
    hadParams.current = false;
    const target = pending;
    setPending(null);
    const next = new URLSearchParams(params);
    next.delete('block');
    next.delete('channel');
    setParams(next, { replace: true });
    if (!target) return; // invalid params are dropped silently
    if (target.channel) setChannel(target.channel);
    const existing = links.find((l) => l.blockRef === target.ref && (!v2 || l.channel === target.channel));
    if (existing) {
      openDraft({ id: existing.id, blockRef: existing.blockRef, blockName: existing.title, description: existing.description, examples: existing.examples.join('\n'), enabled: existing.enabled });
      return;
    }
    openDraft({ ...empty, blockRef: target.ref, blockName: target.ref });
    // The picker finds the name; if it is not listed the ref stays as the name.
    const qs = new URLSearchParams({ q: target.ref, ...(v2 && target.channel ? { channel: target.channel } : {}) });
    void k<{ blocks: PickerBlock[] }>('GET', `/blocks?${qs}`)
      .then((r) => {
        const hit = r.blocks.find((b) => b.ref === target.ref);
        if (hit) setDraft((d) => (d && d.id === null && d.blockRef === target.ref ? { ...d, blockName: hit.name } : d));
      })
      .catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [links]);

  const open = draft !== null;
  useEffect(() => {
    if (!open) return;
    // Only the newest search may fill the list: a slower earlier answer is dropped.
    const seq = ++pickerSeq.current;
    const t = setTimeout(async () => {
      const params = new URLSearchParams({ ...(q.trim() ? { q: q.trim() } : {}), ...(v2 ? { channel } : {}) });
      try {
        const r = await k<{ available: boolean; blocks: PickerBlock[] }>('GET', `/blocks?${params}`);
        if (seq !== pickerSeq.current) return;
        setBlocks(r.blocks);
        setBlocksDown(!r.available);
        setPickerError(null);
      } catch (e) {
        if (seq === pickerSeq.current) setPickerError(errorText(e));
      }
    }, 250);
    return () => clearTimeout(t);
  }, [open, q, channel, v2, k]);

  /** Opens the modal with an empty picker (no list left over from the previous edit). */
  function openDraft(d: Draft) {
    setBlocks(null);
    setBlocksDown(false);
    setPickerError(null);
    setQ('');
    setDraft(d);
  }

  async function act(label: string, fn: () => Promise<unknown>) {
    setBusy(label);
    setError(null);
    try {
      await fn();
      await load();
      return true;
    } catch (e) {
      setError(errorText(e));
      return false;
    } finally {
      setBusy(null);
    }
  }

  /** Recent customer questions → "Add as example": opens that link's editor with the question added (save to keep it). */
  function addExample(linkId: string, text: string) {
    const l = (links ?? []).find((x) => x.id === linkId);
    if (!l) return;
    openDraft({ id: l.id, blockRef: l.blockRef, blockName: l.title, description: l.description, examples: withExample(l.examples, text), enabled: l.enabled });
  }

  async function save() {
    if (!draft) return;
    const body = { blockRef: draft.blockRef, description: draft.description, examples: parseExamples(draft.examples), enabled: draft.enabled };
    const ok = await act('save', () => (draft.id ? k('PUT', `/${draft.id}`, body) : k('POST', '', body)));
    if (ok) setDraft(null);
  }

  async function runTest() {
    setBusy('test');
    setError(null);
    try {
      const r = await k<{ hits: Hit[]; searchedAs?: string | null }>('POST', '/test', { question, ...(v2 ? { channel } : {}) });
      setHits(r.hits);
      setSearchedAs(r.searchedAs ?? null);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(null);
    }
  }

  const bundleSummary = bundleText ? bundleTextSummary(bundleText.bundles, bundleText.truncated) : null;
  const shown = (links ?? []).filter((l) => !v2 || l.channel === channel);
  const draftCount = draft ? distinctExamples(draft.examples) : 0;
  const examplesHint = draft ? exampleLimitHint(draftCount) ?? exampleHint(draftCount) : null;

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">
        Blocks the AI can send. When a customer's question matches a block's description or examples, the AI sends that
        block instead of (or after) its text answer.
      </p>
      {v2 && (
        <div className="flex gap-2 items-center text-sm">
          <span className="text-gray-600">Channel</span>
          <select className={input} value={channel} onChange={(e) => { setChannel(e.target.value); setHits(null); }}>
            {V2_LINK_CHANNELS.map((c) => <option key={c} value={c}>{CHANNEL_LABEL[c]}</option>)}
          </select>
        </div>
      )}
      {!checked && <p className="text-sm text-amber-700">Could not check the bot's blocks right now — a deleted block may not show red yet.</p>}
      {error && <p className="text-sm text-red-600">{error}</p>}
      <div className="flex justify-end">
        <button type="button" className="rounded bg-blue-600 px-3 py-2 text-sm text-white" onClick={() => openDraft(empty)}>
          Add block
        </button>
      </div>
      {bundleTextLoadNotice(bundleTextFailed) && (
        <p id="before-switching-on" className="text-sm text-red-600">{bundleTextLoadNotice(bundleTextFailed)}</p>
      )}
      {bundleSummary && (
        <section id="before-switching-on" className="rounded border border-amber-200 bg-amber-50 p-3 space-y-2">
          <div className="text-sm font-medium text-gray-900">Before switching on</div>
          <p className="text-sm text-gray-700">{bundleSummary.sentence}</p>
          {copyError && <p className="text-sm text-red-600">{copyError}</p>}
          {bundleSummary.missing.length > 0 && (
            <ul className="divide-y divide-amber-200 text-sm">
              {bundleSummary.missing.map((b) => (
                <li key={b.bundleId} className="py-2 flex gap-3 items-start">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-900">{b.name}</div>
                    <div className="text-xs text-gray-600 break-words">{b.textPreview}</div>
                  </div>
                  {copied[b.bundleId] !== undefined ? (
                    <span className="text-xs text-green-700">Copied into “{copied[b.bundleId]}”</span>
                  ) : (
                    <button type="button" disabled={copying !== null} className="text-blue-600 hover:underline whitespace-nowrap"
                      onClick={() => void copyBundle(b)}>
                      Copy into a FAQ document
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
      {links === null ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : shown.length === 0 ? (
        <p className="text-sm text-gray-500">No blocks linked{v2 ? ` on ${CHANNEL_LABEL[channel]}` : ''} yet.</p>
      ) : (
        <ul className="divide-y divide-gray-200 rounded border border-gray-200 bg-white">
          {shown.map((l) => {
            const status = linkStatus(l);
            return (
              <li key={l.id} className="p-3 flex gap-3 items-start">
                <div className="flex-1 min-w-0">
                  <div className={`font-medium ${status === 'broken' ? 'text-red-600' : 'text-gray-900'}`}>
                    {l.title}
                    {status === 'broken' && <span className="text-xs"> — block deleted: re-pick or remove</span>}
                    {status === 'off' && <span className="text-xs text-gray-500"> (off)</span>}
                  </div>
                  <div className="text-sm text-gray-700">{l.description}</div>
                  {l.examples.length > 0 && <div className="text-xs text-gray-500">{l.examples.join(' · ')}</div>}
                  <div className="text-xs text-gray-400">
                    Last sent: {lastSentLabel(l.lastSentAt)}
                    {stats && <> · {linkStatsLabel(stats.links[l.id], stats.days)}</>}
                  </div>
                  {stats && neverClosestHint(l, stats.links[l.id], stats.decisions) && (
                    <div className="text-xs text-amber-700">{neverClosestHint(l, stats.links[l.id], stats.decisions)}</div>
                  )}
                  {descriptionHint(l.description) && <div className="text-xs text-amber-700">{descriptionHint(l.description)}</div>}
                </div>
                <div className="flex gap-3 text-sm">
                  <button type="button" disabled={busy !== null} className="text-blue-600 hover:underline"
                    onClick={() =>
                      openDraft({ id: l.id, blockRef: l.blockRef, blockName: l.title, description: l.description, examples: l.examples.join('\n'), enabled: l.enabled })
                    }>
                    {status === 'broken' ? 'Re-pick' : 'Edit'}
                  </button>
                  {status !== 'broken' && (
                    <button type="button" disabled={busy !== null} className="text-gray-600 hover:underline"
                      onClick={() => void act(l.id, () => k('PUT', `/${l.id}`, { enabled: !l.enabled }))}>
                      {l.enabled ? 'Turn off' : 'Turn on'}
                    </button>
                  )}
                  <button type="button" disabled={busy !== null} className="text-red-600 hover:underline"
                    onClick={() => { if (confirm(`Remove the link to “${l.title}”?`)) void act(l.id, () => k('DELETE', `/${l.id}`)); }}>
                    Remove
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <div className="rounded border border-gray-200 bg-white p-3 space-y-2">
        <div className="text-sm font-medium text-gray-900">Test a question</div>
        <div className="flex gap-2">
          <input className={`${input} flex-1`} value={question} onChange={(e) => setQuestion(e.target.value)} placeholder="e.g. үнэ хэд вэ" />
          <button type="button" disabled={!question.trim() || busy !== null} className="rounded border px-3 text-sm" onClick={() => void runTest()}>
            Test
          </button>
        </div>
        {hits && searchedAs && (
          <p className="text-xs text-gray-600">
            Searched as <span className="font-mono">{searchedAs}</span> — a customer&apos;s Latin is converted the same way first.
          </p>
        )}
        {hits && (hits.length === 0 ? (
          <p className="text-sm text-gray-500">No link found for this question.</p>
        ) : (
          <ul className="text-sm">
            {hits.map((h) => (
              <li key={h.link.id}>
                {scoreLabel(h.score)} — {h.link.title} <span className="text-gray-400">({h.via})</span>{' '}
                <span className="text-xs text-gray-600">{hitBand(h.score, thresholds)}</span>
              </li>
            ))}
          </ul>
        ))}
      </div>

      <RecentBlockQuestions
        system={system}
        botId={botId}
        channel={viewChannel}
        links={links ?? []}
        thresholds={thresholds}
        onTest={(text) => { setQuestion(text); setHits(null); }}
        onAddExample={addExample}
      />

      {draft && (
        <AiConfigModal title={draft.id ? 'Edit block link' : 'Add a block the AI can send'} onClose={() => setDraft(null)} maxWidth="lg">
          <div className="space-y-3">
            <div className="space-y-1">
              <div className="text-sm text-gray-700">Block{v2 ? ` (${CHANNEL_LABEL[channel]})` : ''}</div>
              {draft.blockRef && <div className="text-sm font-medium">{draft.blockName}</div>}
              <input className={`${input} w-full`} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search blocks" />
              {blocksDown && <p className="text-sm text-amber-700">The bot's block list is unavailable right now.</p>}
              {pickerError && <p className="text-sm text-red-600">{pickerError}</p>}
              <ul className="max-h-48 overflow-y-auto text-sm">
                {(blocks ?? []).map((b) => (
                  <li key={b.ref}>
                    <button type="button"
                      className={`w-full text-left px-2 py-1 hover:bg-gray-100 ${b.ref === draft.blockRef ? 'bg-blue-50' : ''}`}
                      onClick={() => setDraft({ ...draft, blockRef: b.ref, blockName: b.name })}>
                      {b.name}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
            <label className="block text-sm text-gray-700">
              When to send it
              <textarea className={`${input} w-full`} rows={3} value={draft.description}
                onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                placeholder="e.g. The customer asks about prices or discounts" />
            </label>
            {draft.description.trim() && descriptionHint(draft.description) && (
              <p className="text-xs text-amber-700">{descriptionHint(draft.description)}</p>
            )}
            <label className="block text-sm text-gray-700">
              Example questions (one per line)
              <textarea className={`${input} w-full`} rows={5} value={draft.examples}
                onChange={(e) => setDraft({ ...draft, examples: e.target.value })} />
            </label>
            {examplesHint && <p className="text-xs text-gray-500">{examplesHint}</p>}
            <CyrillicNote system={system} botId={botId} text={draft.examples} onFix={(examples) => setDraft({ ...draft, examples })} />
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={draft.enabled} onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })} /> On
            </label>
            {error && <p className="text-sm text-red-600">{error}</p>}
            <div className="flex justify-end gap-2">
              <button type="button" className="rounded border px-3 py-2 text-sm" onClick={() => setDraft(null)}>Cancel</button>
              <button type="button" disabled={!draft.blockRef || !draft.description.trim() || exampleLimitHint(draftCount) !== null || busy !== null}
                className="rounded bg-blue-600 px-3 py-2 text-sm text-white disabled:opacity-50" onClick={() => void save()}>
                Save
              </button>
            </div>
          </div>
        </AiConfigModal>
      )}
    </div>
  );
}
